import { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AxiosApiCall from "../../api/AxiosApiCall";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import { handleError } from "../../components/utils/AuthenticationCheck";


const PendingProductStockDetails = () => {
  const [productDetails, setProductDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { stock_id } = location.state || {};

  useEffect(() => {
    const fetchProductStockDetails = async (stock_id) => {
      setLoading(true);
      try {
        const authData = JSON.parse(localStorage.getItem("auth"));
        const token = authData?.token;
        if (!token) throw new Error("No access token found.");

        const response = await axios.get(ApiServices.getProductsStockById, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: { stock_id },
          withCredentials: true,
        });

        const { status, message, data } = response.data;
        if (status === 200) {
          setProductDetails(data);
        } else {
          handleError({ response: { status, data: { message } } }, navigate);
        }
      } catch (error) {
        handleError(error, navigate);
      } finally {
        setLoading(false);
      }
    };

    if (stock_id) {
      fetchProductStockDetails(stock_id);
    } else {
      toast.error("Stock is missing.");
      navigate("/admin/products/stock/list");
    }
  }, [stock_id, navigate]);

  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage] = useState(4);

  const images = productDetails.images || [];
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const axios = AxiosApiCall();

  // Change: Correctly retrieve images
  const allImages = productDetails.images || []; // Updated
  const currentImages = allImages.slice(indexOfFirstImage, indexOfLastImage);
  // console.log("this are the curent images", currentImages);

  const hasNextPage = indexOfLastImage < allImages.length;
  const hasPrevPage = currentPage > 1;



  return (
    <MainLayouts>
      <div className="relative sm:rounded-lg py-6 w-full">
        <section
          className="w-full pr-[3%] pt-3 my-3"
          style={{ backgroundColor: "#F7F7F7" }}
        >
          <nav className="bg-gray-200 py-0 px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex mb-2">
              <li className="flex items-center">
                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                  Pending Stock Product
                </p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Details</p>
              </li>
            </ol>
          </nav>
        </section>

        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <section className="text-gray-600 body-font">
            <div className="container">
              {/* Back Button */}
              <div className="flex justify-end mb-3">
                <Link
                  to="/admin/products/stock/pending"
                  className="mx-2 w-full sm:w-auto text-dGrey3 bg-black2 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  <span className="flex justify-center sm:justify-start mx-2">
                    <ArrowLeftSquare className="mr-2" />
                    Back
                  </span>
                </Link>
              </div>

              {/* Images */}
              <div className="mt-4">
                {images.length > 0 ? (
                  <>
                    {/* Image Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {currentImages.map((image, index) => (
                        <div
                          key={index}
                          className="overflow-hidden shadow-md bg-dGrey3 rounded-lg"
                        >
                          <img
                            src={image.image_url}
                            alt={image.alt_text || `Image ${index}`}
                            className="w-full h-32 object-cover"
                          />
                        </div>
                      ))}
                    </div>


                    {/* Prev & Next Buttons */}
                    <div className="flex justify-end mt-6">
                      <nav aria-label="Page navigation">
                        <ul className="pagination flex mx-auto">
                          <li
                            className={`page-item ${!hasPrevPage ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="text-dGrey3 bg-black2 hover:bg-darkGreen2 font-medium rounded-full text-sm px-5 py-2"
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={!hasPrevPage}
                            >
                              Prev
                            </button>
                          </li>
                          <li
                            className={`page-item ${!hasNextPage ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="text-dGrey3 bg-black2 hover:bg-darkGreen2 font-medium rounded-full text-sm px-5 py-2"
                              onClick={() => setCurrentPage((prev) => prev + 1)}
                              disabled={!hasNextPage}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </>
                ) : (
                  <div className="col-span-full text-center">
                    No images available.
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="col-span-12 lg:col-span-12 p-2 mt-6">
                <div className="flex items-center border-dash border p-3 rounded-lg border-l-4">
                  <section className="w-full">
                    <h2 className="text-sm title-font mt-2">
                      {productDetails.name || "Not Available"}
                    </h2>
                    <h1 className="text-dash text-3xl font-medium mb-1">
                      {productDetails.category || "Not Available"}
                    </h1>
                    <span className="bg-dash text-dGrey3 text-xs font-medium px-3 py-1 rounded-full mr-2">
                      {productDetails.status || "Not Available"}
                    </span>
                    <span className="bg-orange1 text-dGrey3 text-xs font-medium px-3 py-1 rounded-full">
                      {productDetails.store_name || "Not Available"}
                    </span>
                    <p className="leading-relaxed mb-4 mt-3">
                      {productDetails.description || "Not Available"}
                    </p>
                  </section>
                </div>
              </div>

              {/* Variants */}
              <div className="p-2 w-full mt-4">
                <h1 className="text-dash md:text-lg">Stock Variants</h1>
                <div className="flex items-center border-dash p-1">
                  <div className="shadow-md sm:rounded-lg w-full overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-500">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Name</th>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Value</th>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Quantity</th>
                          {/* <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Images</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {productDetails.stock_variants?.length > 0 ? (
                          productDetails.stock_variants.map((variant, index) => (
                            <tr key={index} className="odd:bg-white even:bg-gray-50 border-b">
                              <td className="px-6 py-4">{variant.name}</td>
                              <td className="px-6 py-4">{variant.value}</td>
                              <td className="px-6 py-4">{variant.quantity}</td>
                              {/* <td className="px-6 py-4 flex gap-2 flex-wrap">
                                {variant.images?.map((img, i) => (
                                  <img
                                    key={i}
                                    src={img.image_url}
                                    alt={img.alt_text}
                                    className="w-16 h-16 object-cover rounded"
                                  />
                                ))} 
                            </td>*/}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-4">
                              No variants available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Properties */}
              <div className="p-2 w-full mt-4">
                <h1 className="text-dash md:text-lg">Product Properties</h1>
                <div className="flex items-center border-dash p-1">
                  <div className="shadow-md sm:rounded-lg w-full overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-500">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Name</th>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Description</th>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Unit</th>
                          <th className="px-6 py-3 bg-darkGreen3 text-lGrey">Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productDetails.properties?.length > 0 ? (
                          productDetails.properties.map((prop, index) => (
                            <tr key={index} className="odd:bg-white even:bg-gray-50 border-b">
                              <td className="px-6 py-4">{prop.name}</td>
                              <td className="px-6 py-4">{prop.description}</td>
                              <td className="px-6 py-4">{prop.unit}</td>
                              <td className="px-6 py-4">{prop.options?.join(", ")}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-4">
                              No properties available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </MainLayouts >
  );
};

export default PendingProductStockDetails;
